/**
 * Scripted mock game: Buffalo at Carolina (preseason).
 *
 * SIMULATED DATA. This is not a real NFL game and never claims to be:
 * every surface that renders it shows a "Simulated game" badge.
 *
 * The script contains only what actually happened on each play. Score,
 * down, distance, possession and ball position are all *derived* by
 * `utils/gameEngine.js`, so the numbers are internally consistent rather
 * than hand-typed (Implementation Guide §24).
 *
 * Descriptions deliberately never name a player: the MVP has no verified
 * roster source, and inventing names would be fabricating data.
 *
 * The sequence covers every event type in PRD §32:
 * run, pass, incomplete, first down, penalty, punt, field goal,
 * touchdown, interception, sack, kickoff, extra point.
 */

import { EVENT_TYPES as E } from "../concepts/eventConceptMap";

const BUF = "BUF";
const CAR = "CAR";

/* --- terse constructors so the script stays readable --- */
const run = (teamId, yards, description, clockUsed = 40) => ({
  type: E.RUN,
  teamId,
  yards,
  description,
  clockUsed,
});

const pass = (teamId, yards, description, clockUsed = 38) => ({
  type: E.PASS_COMPLETE,
  teamId,
  yards,
  description,
  clockUsed,
});

const inc = (teamId, description = "The pass falls incomplete.", clockUsed = 8) => ({
  type: E.PASS_INCOMPLETE,
  teamId,
  description,
  clockUsed,
});

const sack = (teamId, yards, description, clockUsed = 40) => ({
  type: E.SACK,
  teamId,
  yards,
  description,
  clockUsed,
});

const punt = (teamId, puntYards, description, clockUsed = 15) => ({
  type: E.PUNT,
  teamId,
  puntYards,
  description,
  clockUsed,
});

const kickoff = (receivingTeamId, resultPosition, description, clockUsed = 10) => ({
  type: E.KICKOFF,
  receivingTeamId,
  resultPosition,
  description,
  clockUsed,
});

const fieldGoal = (teamId, description, clockUsed = 10) => ({
  type: E.FIELD_GOAL,
  teamId,
  description,
  clockUsed,
});

const extraPoint = (teamId, clockUsed = 5) => ({
  type: E.EXTRA_POINT,
  teamId,
  description: "The extra point is good.",
  clockUsed,
});

const interception = (teamId, description, clockUsed = 12) => ({
  type: E.INTERCEPTION,
  teamId,
  description,
  clockUsed,
});

const fumble = (teamId, description, clockUsed = 12) => ({
  type: E.FUMBLE,
  teamId,
  description,
  clockUsed,
});

const penalty = (teamId, yards, description, options = {}, clockUsed = 20) => ({
  type: E.PENALTY,
  teamId,
  yards,
  description,
  clockUsed,
  ...options,
});

const endQuarter = (clockUsed = 0) => ({
  type: E.END_OF_QUARTER,
  description: "That's the end of the quarter.",
  clockUsed,
});

const endGame = () => ({
  type: E.END_OF_GAME,
  description: "That's the game.",
  clockUsed: 0,
});

/* ------------------------------------------------------------------ */

export const BUFFALO_AT_CAROLINA_SCRIPT = Object.freeze([
  /* ============================== Q1 ============================== */
  kickoff(BUF, 25, "Carolina kicks off. Buffalo starts from their own 25."),

  run(BUF, 4, "Handoff up the middle for a short gain."),
  pass(BUF, 8, "Quick pass to the right for 8 yards."),
  inc(BUF, "The pass sails over the receiver's head."),
  run(BUF, 3, "Run to the left side for 3 yards."),
  pass(BUF, 12, "Pass over the middle picks up 12."),
  sack(BUF, 7, "Pressure gets home: the quarterback is dragged down for a 7-yard loss."),
  inc(BUF, "Under pressure again, the ball is thrown away."),
  pass(BUF, 6, "Short checkdown pass for 6 yards."),
  punt(BUF, 42, "Buffalo punts it away and pins Carolina deep."),

  run(CAR, 5, "Carolina starts with a run for 5 yards."),
  inc(CAR, "The pass is broken up at the last second."),
  pass(CAR, 7, "Pass to the sideline moves the chains."),
  run(CAR, 2, "Run stuffed after 2 yards."),
  penalty(CAR, -10, "Holding on the offence. Ten yards back, and the down is replayed."),
  pass(CAR, 9, "Screen pass gains 9."),
  inc(CAR, "Deep ball down the sideline, incomplete."),
  punt(CAR, 45, "Carolina punts."),

  run(BUF, 6, "Run off the right side for 6."),
  pass(BUF, 14, "Pass down the middle for 14 yards."),
  run(BUF, 3, "Run for 3."),
  pass(BUF, 7, "Pass to the tight end for 7 yards."),
  pass(BUF, 11, "Crossing route picks up 11."),
  run(BUF, 4, "Run to the left for 4."),
  inc(BUF, "The throw into the end zone is broken up."),
  pass(BUF, 6, "Pass to the flat for 6 yards."),
  run(BUF, 5, "Run inside to the 9-yard line."),
  pass(BUF, 9, "Touchdown! Pass caught in the back of the end zone.", 36),
  extraPoint(BUF),

  kickoff(CAR, 25, "Buffalo kicks off. Carolina takes it out to the 25."),
  run(CAR, 3, "Run for 3 yards."),
  endQuarter(),

  /* ============================== Q2 ============================== */
  inc(CAR, "Pass batted down at the line."),
  pass(CAR, 9, "Pass to the sideline for 9, and a first down."),
  run(CAR, 5, "Run for 5 yards."),
  pass(CAR, 6, "Quick slant for 6."),
  inc(CAR, "Overthrown down the seam."),
  sack(CAR, 8, "Sacked for a loss of 8."),
  pass(CAR, 10, "Pass underneath gets 10 back."),
  punt(CAR, 38, "Carolina punts from midfield."),

  run(BUF, 4, "Run out of their own end zone shadow for 4."),
  pass(BUF, 7, "Pass for 7 yards and a first down."),
  interception(BUF, "Intercepted! The throw is jumped and Carolina takes over in Buffalo territory."),

  run(CAR, 5, "Run for 5 yards."),
  inc(CAR, "Fade to the corner falls incomplete."),
  pass(CAR, 3, "Short pass sets up fourth and short."),
  fieldGoal(CAR, "The field goal is up... and good. Three points."),

  kickoff(BUF, 25, "Carolina kicks off after the field goal."),
  run(BUF, 8, "Big run for 8 yards."),
  run(BUF, 4, "Run for 4 and a first down."),
  pass(BUF, 18, "Deep completion for 18 yards."),
  penalty(
    BUF,
    5,
    "Defensive holding. Five yards and an automatic first down for Buffalo.",
    { automaticFirstDown: true }
  ),
  inc(BUF, "Incomplete on first down."),
  run(BUF, 2, "Run for a short gain."),
  pass(BUF, 8, "Third-down conversion for 8 yards."),
  run(BUF, 3, "Run for 3."),
  fumble(BUF, "Fumble! The ball is knocked loose and Carolina recovers."),

  run(CAR, 6, "Run for 6 yards."),
  pass(CAR, 12, "Pass for 12 and a first down."),
  inc(CAR, "Pass dropped in the flat."),
  pass(CAR, 14, "Pass down the sideline for 14."),
  run(CAR, 4, "Run for 4 yards."),
  inc(CAR, "Incomplete: the receiver couldn't get his feet down."),
  pass(CAR, 9, "Pass for 9 yards and a first down.", 30),
  fieldGoal(CAR, "Another field goal. Carolina within one.", 11),
  endQuarter(),

  /* ============================== Q3 ============================== */
  kickoff(CAR, 25, "Second half. Carolina receives."),
  run(CAR, 5, "Run for 5 to open the half."),
  inc(CAR, "Pass broken up."),
  pass(CAR, 22, "Big gain: 22 yards on third down."),
  run(CAR, 3, "Run for 3."),
  pass(CAR, 9, "Pass for 9 and a first down."),
  run(CAR, 6, "Run for 6."),
  pass(CAR, 30, "Touchdown Carolina! Thirty yards through the middle of the defence.", 30),
  extraPoint(CAR),

  kickoff(BUF, 25, "Carolina kicks off."),
  run(BUF, 2, "Run for 2."),
  inc(BUF, "Pass tipped and incomplete."),
  sack(BUF, 6, "Sacked again: a six-yard loss sets up a long fourth down.", 35),
  punt(BUF, 48, "A long punt flips the field."),

  run(CAR, 4, "Run for 4."),
  pass(CAR, 5, "Pass for 5 sets up third and short."),
  run(CAR, 1, "Run for one yard and a first down."),
  inc(CAR, "Pass incomplete."),
  penalty(CAR, -5, "False start. Five yards back before the snap."),
  pass(CAR, 7, "Pass for 7 yards."),
  inc(CAR, "Third-down pass falls incomplete. The punt team comes on."),
  punt(CAR, 40, "Carolina punts it back."),

  pass(BUF, 14, "Pass for 14 yards and a first down."),
  run(BUF, 7, "Run for 7."),
  run(BUF, 5, "Run for 5 and another first down."),
  pass(BUF, 16, "Pass for 16 yards into Carolina territory."),
  inc(BUF, "Incomplete over the middle."),
  run(BUF, 6, "Run for 6 yards."),
  pass(BUF, 12, "Third-down conversion for 12."),
  run(BUF, 5, "Run for 5 yards."),
  inc(BUF, "Incomplete in the end zone."),
  pass(BUF, 5, "Pass for 5 and a first down inside the 15."),
  endQuarter(16),

  /* ============================== Q4 ============================== */
  run(BUF, 4, "Run for 4 to open the quarter."),
  inc(BUF, "Incomplete: thrown away under pressure."),
  pass(BUF, 9, "Touchdown Buffalo! Pass into the corner of the end zone.", 36),
  extraPoint(BUF),

  kickoff(CAR, 25, "Buffalo kicks off with the lead."),
  run(CAR, 3, "Run for 3."),
  pass(CAR, 8, "Pass for 8 and a first down."),
  inc(CAR, "Pass broken up on first down."),
  sack(CAR, 9, "Sacked for a nine-yard loss."),
  pass(CAR, 11, "Pass for 11, still short of the sticks."),
  punt(CAR, 44, "Carolina has to punt."),

  run(BUF, 5, "Run for 5 yards."),
  run(BUF, 6, "Run for 6 and a first down."),
  inc(BUF, "Incomplete."),
  run(BUF, 3, "Run for 3."),
  inc(BUF, "Third-down pass is incomplete."),
  punt(BUF, 45, "Buffalo punts, playing for field position."),

  pass(CAR, 12, "Pass for 12 and a first down."),
  run(CAR, 4, "Run for 4."),
  pass(CAR, 18, "Pass for 18 across midfield."),
  run(CAR, 2, "Run for 2."),
  inc(CAR, "Incomplete."),
  pass(CAR, 9, "Pass for 9 and a first down in Buffalo territory."),
  run(CAR, 3, "Run for 3."),
  interception(CAR, "Intercepted! Buffalo picks it off and the drive is over."),

  run(BUF, 4, "Run for 4, clock running."),
  run(BUF, 7, "Run for 7 and a first down."),
  run(BUF, 3, "Run for 3."),
  pass(BUF, 9, "Pass for 9 and another first down."),
  run(BUF, 2, "Run for 2, clock winding down."),
  run(BUF, 1, "Run for 1 to run out the clock.", 27),
  endGame(),
]);

export default BUFFALO_AT_CAROLINA_SCRIPT;
